import React, {
  useState,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from "react";
import DesktopDateStep from "./date-step/DesktopDateStep";
import MobileDateStep from "./date-step/MobileDateStep";
import { useClinics } from "@/hooks/useClinics";
import { ExpandableOrderSummary } from "./booking/ExpandableOrderSummary";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { addMonths, addDays, startOfMonth, format } from "date-fns";
import {
  isDateDisabled as isDateDisabledUtil,
  getFirstAvailableDate as getFirstAvailableDateUtil,
} from "@/utils/dateUtils";
import { t } from "@/locales";
import {
  useClinicExternalSlots,
  hasExternalSlots as hasExternalSlotsUtil,
} from "@/hooks/useClinicExternalSlots";
import { useMembership } from "@/contexts/MembershipContext";

// Tailwind `lg` breakpoint = 1024px
const LG_BREAKPOINT = 1024;
const subscribeResize = (cb: () => void) => {
  window.addEventListener("resize", cb);
  return () => window.removeEventListener("resize", cb);
};
const getIsDesktop = () => window.innerWidth >= LG_BREAKPOINT;
const getIsDesktopServer = () => false;

const isSameDay = (dateA: Date, dateB: Date) =>
  dateA.getDate() === dateB.getDate() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getFullYear() === dateB.getFullYear();

// This component now uses the bookingFlowStore directly - NO PROPS NEEDED!
// All the complex validation logic has been moved to the store
type DateStepProps = Record<string, never>;

const DateStep = (_props: DateStepProps) => {
  const isDesktop = useSyncExternalStore(
    subscribeResize,
    getIsDesktop,
    getIsDesktopServer,
  );

  // Get all state and actions from the booking flow store
  const {
    language,
    selectedDate: storeDateValue,
    selectDate,
    selectedTimeSlots,
    selectTimeSlot,
    deleteTimeSlot,
    bookingType,
    setBookingType,
    selectedClinic,
    goToStep,
    goBack,
    canGoBack,
    canProceed,
    selectedProductDetails,
    selectedAddOnDetails,
    firstAvailableDate: storeFirstAvailableDate,
    removeProduct,
    toggleAddOn,
  } = useBookingFlow();

  // Convert null to undefined for component compatibility
  const selectedDate = storeDateValue ?? undefined;

  const { data: clinics } = useClinics();
  const { getEffectivePrice } = useMembership();
  const currentClinic = clinics?.find((c) => c.id === selectedClinic);
  const fastBookingSlots = currentClinic?.fast_booking_slots as
    | import("@/types/booking").FastBookingSlots
    | undefined;

  // Fetch external slots for clinics with external booking systems (e.g., Hirslanden)
  const { data: externalSlots, isLoading: isLoadingExternalSlots } =
    useClinicExternalSlots(selectedClinic);

  const hasExternalSlots = hasExternalSlotsUtil(externalSlots);

  // Check if any selected add-on has a warning — when true, we can't reliably
  // predict instant vs request slots so we hide those indicators on time buttons
  const hasAddOnWarnings = selectedAddOnDetails.some((addon) => addon.warning);

  // Instant-vs-request indicators only mean something where BOTH kinds exist:
  // the clinic syncs slots AND takes bookings outside them. Which of those is
  // true is now a property of the clinic, not a guess from how many slots the
  // partner published this month (see clinics.accepts_request_slots).
  const clinicHasSlotIndicators =
    hasExternalSlots && !!currentClinic?.accepts_request_slots;

  // Determine if we should show slot availability indicators (instant vs request)
  // Hide when add-ons with warnings are selected (slot predictions unreliable)
  const showSlotIndicators = clinicHasSlotIndicators && !hasAddOnWarnings;

  // A clinic that does not take requests offers exactly what it published.
  const useExternalSlotsOnly = hasExternalSlots && !clinicHasSlotIndicators;

  // Centralized expanded state for slot preference blocks
  // If user already has a first time slot, start collapsed (no expand-then-collapse animation)
  const [isFirstSlotExpanded, setIsFirstSlotExpanded] = useState(
    () => !selectedTimeSlots[0],
  );
  const [isSecondSlotExpanded, setIsSecondSlotExpanded] = useState(
    () => !!selectedTimeSlots[0] && !selectedTimeSlots[1],
  );

  // Calculate first available date considering external slots
  // This overrides the store's calculation when external slots are available
  const firstAvailableDate = React.useMemo(() => {
    if (clinicHasSlotIndicators) {
      // When clinic has slot indicators, use base restrictions only (no external slots)
      // This matches the calendar's isDateDisabled logic which also ignores external slots
      return getFirstAvailableDateUtil(
        currentClinic || null,
        selectedProductDetails,
        selectedAddOnDetails,
        undefined,
      );
    }
    if (externalSlots && externalSlots.length > 0) {
      // Use the utility function with external slots
      return getFirstAvailableDateUtil(
        currentClinic || null,
        selectedProductDetails,
        selectedAddOnDetails,
        externalSlots,
      );
    }
    // Fall back to store's calculation when no external slots
    return storeFirstAvailableDate;
  }, [
    clinicHasSlotIndicators,
    currentClinic,
    selectedProductDetails,
    selectedAddOnDetails,
    externalSlots,
    storeFirstAvailableDate,
  ]);

  // Auto-select first available date when arriving at date step
  // Also re-select if current date becomes disabled (e.g., when external slots load)
  React.useEffect(() => {
    console.log(
      "[DateStep] Effect running - selectedDate:",
      selectedDate,
      "firstAvailableDate:",
      firstAvailableDate,
      "isLoadingExternalSlots:",
      isLoadingExternalSlots,
    );

    // Don't auto-select while external slots are still loading to prevent flash
    if (isLoadingExternalSlots) {
      console.log("[DateStep] External slots still loading, waiting...");
      return;
    }

    if (!firstAvailableDate) return;

    // Case 1: No date selected yet
    if (!selectedDate) {
      console.log(
        "[DateStep] No date selected, auto-selecting first available:",
        firstAvailableDate,
      );
      selectDate(firstAvailableDate, { isManual: false });
      return;
    }

    // Case 2: Selected date is before the first available date (e.g., external slots shifted it forward)
    if (selectedDate < firstAvailableDate) {
      console.log(
        "[DateStep] Selected date is before first available, auto-correcting:",
        firstAvailableDate,
      );
      selectDate(firstAvailableDate, { isManual: false });
      return;
    }

    // Case 3: Current date is disabled (no slots available) - only check when external slots are loaded
    // Skip this check when clinic has slot indicators because we allow all dates (except weekends)
    if (!clinicHasSlotIndicators && externalSlots && externalSlots.length > 0) {
      const isCurrentDateDisabled = isDateDisabledUtil(
        selectedDate,
        currentClinic,
        selectedProductDetails,
        selectedAddOnDetails,
        externalSlots,
      );

      if (isCurrentDateDisabled) {
        console.log(
          "[DateStep] Current date is disabled, auto-selecting first available:",
          firstAvailableDate,
        );
        selectDate(firstAvailableDate, { isManual: false });
      }
    }
  }, [
    selectedDate,
    firstAvailableDate,
    selectDate,
    externalSlots,
    currentClinic,
    selectedProductDetails,
    selectedAddOnDetails,
    isLoadingExternalSlots,
    clinicHasSlotIndicators,
  ]);

  // Stable max date — only changes when the calendar day rolls over
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const maxDate = useMemo(() => addMonths(new Date(todayKey), 3), [todayKey]);

  // Show loading state while fetching external slots to prevent flash
  // This only affects clinics that have external booking systems (e.g., Hirslanden)
  const isLoading = isLoadingExternalSlots;

  console.log("[DateStep] Rendering with:", {
    selectedDate,
    selectedTimeSlots,
    firstAvailableDate,
    canProceed,
  });

  // Calculate total price using member-effective prices so the collapsed
  // summary bar matches the per-item discounted prices shown above.
  const totalPrice = [
    ...selectedProductDetails,
    ...selectedAddOnDetails,
  ].reduce((sum, product) => sum + getEffectivePrice(product), 0);

  // Check if this is a blood-only booking
  const isBloodOnlyBooking = selectedProductDetails.some(
    (p) =>
      p.product_type === "package" && p.package_types?.includes("blood-only"),
  );

  // Pre-compute disabled dates for the visible calendar range (O(n) once, then O(1) lookups)
  // For clinicHasSlotIndicators mode, also pre-compute the base-only set
  const { disabledDatesSet, baseDisabledDatesSet } = React.useMemo(() => {
    const disabled = new Set<string>();
    const baseDisabled = new Set<string>();
    const start = startOfMonth(firstAvailableDate || new Date());
    let d = start;
    while (d <= maxDate) {
      if (
        isDateDisabledUtil(
          d,
          currentClinic,
          selectedProductDetails,
          selectedAddOnDetails,
          externalSlots,
        )
      ) {
        disabled.add(format(d, "yyyy-MM-dd"));
      }
      if (
        clinicHasSlotIndicators &&
        isDateDisabledUtil(
          d,
          currentClinic,
          selectedProductDetails,
          selectedAddOnDetails,
          undefined,
        )
      ) {
        baseDisabled.add(format(d, "yyyy-MM-dd"));
      }
      d = addDays(d, 1);
    }
    return { disabledDatesSet: disabled, baseDisabledDatesSet: baseDisabled };
  }, [
    currentClinic,
    selectedProductDetails,
    selectedAddOnDetails,
    externalSlots,
    firstAvailableDate,
    maxDate,
    clinicHasSlotIndicators,
  ]);

  const isDateDisabled = useCallback(
    (date: Date) => disabledDatesSet.has(format(date, "yyyy-MM-dd")),
    [disabledDatesSet],
  );

  const hasTimeSlotForDate = useCallback(
    (date: Date) =>
      selectedTimeSlots.some((slot) => isSameDay(slot.date, date)),
    [selectedTimeSlots],
  );

  const createIsDateDisabled = useCallback(
    (excludeSlotIndex?: number) => (date: Date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      if (clinicHasSlotIndicators) {
        if (baseDisabledDatesSet.has(dateKey)) return true;
      } else {
        if (disabledDatesSet.has(dateKey)) return true;
      }
      return selectedTimeSlots.some((slot, index) => {
        if (excludeSlotIndex !== undefined && index === excludeSlotIndex)
          return false;
        return isSameDay(slot.date, date);
      });
    },
    [
      clinicHasSlotIndicators,
      baseDisabledDatesSet,
      disabledDatesSet,
      selectedTimeSlots,
    ],
  );

  // Use centralized translations
  const translations = {
    back: t(language, "common.prev"),
    continue: t(language, "common.next"),
    title: t(language, "dateStep.title"),
    description: t(language, "dateStep.description"),
  };

  const orderSummaryTranslations = {
    orderSummary: t(language, "orderSummary.title"),
    scan: t(language, "orderSummary.scan"),
    addOns: t(language, "orderSummary.addOns"),
    bloodTests: t(language, "orderSummary.bloodTests"),
    location: t(language, "orderSummary.location"),
    timeSlots: t(language, "orderSummary.timeSlots"),
    totalPrice: t(language, "orderSummary.totalPrice"),
    continue: t(language, "common.next"),
    back: t(language, "common.prev"),
    remove: t(language, "common.remove"),
  };

  // Handler for continue button
  const handleContinue = () => {
    console.log("[DateStep] Continue clicked");
    // Store's goToStep handles validation automatically
    goToStep("details");
  };

  return (
    <>
      <div className="w-full">
        {/* Header Section */}
        <div className="text-center mb-10 lg:mb-8 px-4 hidden lg:block">
          <h1
            className="mb-2"
            style={{
              fontWeight: 400,
              color: "#2D2D2D",
              fontSize: 24,
            }}
          >
            {translations.title}
          </h1>
          {/* Hide description for blood-only bookings (instant confirmation) */}
          {!isBloodOnlyBooking && (
            <p
              className="mx-auto leading-relaxed"
              style={{ fontSize: 14, fontWeight: 400, color: "#2D2D2D" }}
            >
              {translations.description}
            </p>
          )}
        </div>

        {/* Render only the layout that matches the viewport to avoid double render trees */}
        {isDesktop ? (
          <div className="flex justify-center px-8 lg:min-h-[400px]">
            <div className="max-w-5xl w-full">
              <DesktopDateStep
                currentDate={selectedDate}
                onDateSelect={selectDate}
                fromDate={firstAvailableDate}
                maxDate={maxDate}
                language={language}
                selectedTimeSlots={selectedTimeSlots}
                getIsDateDisabled={createIsDateDisabled}
                hasTimeSlotForDate={hasTimeSlotForDate}
                bookingType={bookingType}
                selectedClinic={selectedClinic}
                onTimeSelect={selectTimeSlot}
                onDeleteTimeSlot={deleteTimeSlot}
                setCurrentStep={goToStep}
                setBookingType={setBookingType}
                onBack={goBack}
                fastBookingSlots={fastBookingSlots}
                isLoading={isLoading}
                showSlotIndicators={showSlotIndicators}
                useExternalSlotsOnly={useExternalSlotsOnly}
                externalSlots={externalSlots}
                controlledFirstSlotExpanded={isFirstSlotExpanded}
                onFirstSlotExpandedChange={setIsFirstSlotExpanded}
                controlledSecondSlotExpanded={isSecondSlotExpanded}
                onSecondSlotExpandedChange={setIsSecondSlotExpanded}
              />
            </div>
          </div>
        ) : (
          <MobileDateStep
            currentDate={selectedDate}
            onDateSelect={selectDate}
            fromDate={firstAvailableDate}
            maxDate={maxDate}
            language={language}
            selectedTimeSlots={selectedTimeSlots}
            getIsDateDisabled={createIsDateDisabled}
            hasTimeSlotForDate={hasTimeSlotForDate}
            bookingType={bookingType}
            selectedClinic={selectedClinic}
            onTimeSelect={selectTimeSlot}
            onDeleteTimeSlot={deleteTimeSlot}
            setCurrentStep={goToStep}
            setBookingType={setBookingType}
            onBack={goBack}
            fastBookingSlots={fastBookingSlots}
            isLoading={isLoading}
            showSlotIndicators={showSlotIndicators}
            useExternalSlotsOnly={useExternalSlotsOnly}
            externalSlots={externalSlots}
            controlledFirstSlotExpanded={isFirstSlotExpanded}
            onFirstSlotExpandedChange={setIsFirstSlotExpanded}
            controlledSecondSlotExpanded={isSecondSlotExpanded}
            onSecondSlotExpandedChange={setIsSecondSlotExpanded}
          />
        )}
      </div>

      {/* Expandable Order Summary Footer */}
      <ExpandableOrderSummary
        totalPrice={totalPrice}
        selectedProducts={selectedProductDetails}
        selectedAddOns={selectedAddOnDetails}
        selectedClinic={currentClinic}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        onContinue={canProceed ? handleContinue : undefined}
        onBack={goBack}
        showBackButton={true}
        showContinueButton={true}
        continueDisabled={!canProceed}
        backDisabled={!canGoBack}
        onProductRemove={removeProduct}
        onAddOnRemove={toggleAddOn}
        onEditProducts={() => goToStep("product", { skipValidation: true })}
        onEditAddOns={() => goToStep("addons", { skipValidation: true })}
        onEditLocation={() => goToStep("clinic", { skipValidation: true })}
        onEditTimeSlots={() => goToStep("date", { skipValidation: true })}
        translations={orderSummaryTranslations}
        currentStep="date"
      />
    </>
  );
};

export default DateStep;
